import {StyleSheet, View, Pressable} from 'react-native';
import type {BundleType} from 'src/types/TransactionDetail';
import AVFormattedCurrency from '../../elements/AVFormattedCurrency';
import Styles from '../../Styles';
import Localized from 'src/constants/AppStrings';
import {CartItemModifier} from 'src/types/Cart';
import AccountStore from 'src/stores/AccountStore';
import SupLeafIcon from 'src/components/img/svg/SupLeafIcon';
import AVText from 'src/components/elements/AVText';
import TrashIcon from 'src/components/img/svg/TrashIcon';
import TagLightIcon from 'src/components/img/svg/TagLightIcon';
import DeleteIcon from 'src/components/img/svg/DeleteIcon';
import {scanAndPayTemplate} from './templates/scanandPayThemeHelper';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

type CartBundleProps = {
  trashClick?: (lineNumbers: Array<number>) => void;
  bundle: BundleType;
};

const CartItemBundle = (props: CartBundleProps): JSX.Element => {
  const cartTrashClick = (lineNumbers: Array<number>) => {
    props.trashClick?.(lineNumbers);
  };

  const totalDiscount = props.bundle.items.reduce((prev, cur) => {
    if (cur.Discount) {
      return prev + parseFloat(cur.Discount) * 1;
    } else {
      return prev;
    }
  }, 0);

  const totalPrice = props.bundle.items.reduce((prev, cur) => {
    const price = parseFloat(cur.Price ?? '');
    let modifierPrice = 0;

    if (cur.Modifiers) {
      modifierPrice = cur.Modifiers.reduce(
        (prevModifierPrice: number, modifier: CartItemModifier) => {
          if (modifier.Price) {
            return prevModifierPrice + parseFloat(modifier.Price.toString());
          } else {
            return prevModifierPrice;
          }
        },
        0,
      );
    }

    return prev + price + modifierPrice;
  }, 0);

  const items = props.bundle.items.map((item) => {
    const icon = (
      <View style={styles.tag}>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
          accessibilityRole="text"
          accessible={true}
          accessibilityLabel={item.Description}
          aria-label={item.Description}
          style={styles.tagText}
          numberOfLines={3}
          ellipsizeMode="tail"
          testID="qa_Checkout.ProductName_txt"
          nativeID="qa_Checkout.ProductName_txt"
        >
          {`${item.Description || ''}`}
        </AVText>
        <DeleteIcon
          style={styles.promoDeleteIcon}
          accessible
          testID="qa_Checkout.DeleteBundle_btn"
        />
      </View>
    );
    return (
      <>
        <Pressable
          accessible={true}
          nativeID="Cart Promo Delete product"
          accessibilityLabel="Cart Promo Delete product"
          accessibilityRole="button"
          accessibilityHint={`Cart Promo Double tap to delete ${
            item?.Name ?? ' this item '
          } from your cart`}
          role="button"
          aria-label="Cart Promo Delete product"
          style={styles.categoryButton}
          onPress={() => cartTrashClick([item.LineNumber || -1])}
        >
          {icon}
        </Pressable>
      </>
    );
  });

  const _totalSupFee = props.bundle.items.reduce(
    (acc, detail) => acc + (detail.TransactionChargeAmount || 0),
    0,
  );

  const handleBundleTrashClick = () => {
    const lineNumbers = props.bundle.items.map((item) => item.LineNumber || -1);
    cartTrashClick(lineNumbers);
  };

  return (
    <View style={{marginTop: Styles.Spacing.m2}}>
      <View style={styles.promoContainer}>
        <View
          style={[
            styles.column,
            scanAndPayTemplate.checkoutStyles.promoTrashIcon,
          ]}
        >
          <Pressable
            accessible={true}
            accessibilityLabel="Delete bundle products"
            accessibilityHint={'Delete bundle products from your cart'}
            onPress={handleBundleTrashClick}
            testID="qa_Checkout.DeleteProduct_btn"
            nativeID="qa_Checkout.DeleteProduct_btn"
          >
            <TrashIcon
              size={scanAndPayTemplate.deleteIconDimension}
              color={scanAndPayTemplate.deleteIconColor}
            />
          </Pressable>
        </View>
        <View
          style={[
            styles.column,
            scanAndPayTemplate.checkoutStyles.promoDescriptionMain,
          ]}
        >
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            accessibilityRole="text"
            accessible={true}
            accessibilityLabel={Localized.Labels.promo_item_text}
            aria-label={Localized.Labels.promo_item_text}
            numberOfLines={1}
            style={[
              styles.cartSupLine,
              styles.promoText,
              scanAndPayTemplate.checkoutStyles.bundlePromoLabelMargin,
            ]}
          >
            {items?.length} {Localized.Labels.promo_item_text}
          </AVText>
          <View
            style={[
              styles.promoNameItemName,
              scanAndPayTemplate.checkoutStyles.bundlePromoView,
            ]}
          >
            <View style={styles.promoTagIcon}>
              <TagLightIcon
                fill={scanAndPayTemplate.discountTagColor}
                width={scanAndPayTemplate.discountTagDimensions.width}
                height={scanAndPayTemplate.discountTagDimensions.height}
              />
            </View>
            <View
              style={styles.promoNameDesc}
              accessible
              testID="qa_Checkout.OnScreenDiscount_txt"
              nativeID="qa_Checkout.OnScreenDiscount_txt"
            >
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                accessible={true}
                accessibilityLabel={`${
                  props.bundle.title
                }, $${totalPrice.toFixed(2)}`}
                accessibilityRole="text"
                aria-label={`${props.bundle.title}, $${totalPrice.toFixed(
                  2,
                )}, text`}
                numberOfLines={6}
                style={[
                  styles.promoDesc,
                  scanAndPayTemplate.cartPromoTextMargin,
                ]}
              >
                {props.bundle.title.substring(0, 30) + ' '}
                (-
                {
                  <AVFormattedCurrency
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
                    style={styles.promoDesc}
                    value={totalDiscount}
                    currency={AccountStore.getCurrency()}
                    numberOfLines={1}
                  />
                }
                )
              </AVText>
            </View>
          </View>
        </View>
        <View
          style={[styles.column, styles.promoAmountMainView]}
          accessible
          testID="qa_Checkout.BundleGroupDiscount_txt"
          nativeID="qa_Checkout.BundleGroupDiscount_txt"
        >
          <AVFormattedCurrency
            style={[styles.amount]}
            value={totalPrice}
            accessible={true}
            accessibilityLabel={`amount $${totalPrice}`}
            aria-label={`amount $${totalPrice}, text`}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            currency={AccountStore.getCurrency()}
            numberOfLines={1}
          />
          {scanAndPayTemplate.toggleStylesAndCurrencies && (
            <AVFormattedCurrency
              style={styles.priceAmountLine}
              value={totalPrice - totalDiscount}
              accessible={true}
              accessibilityLabel={`Discount amount $${
                totalPrice - totalDiscount
              }`}
              aria-label={`Discount amount $${
                totalPrice - totalDiscount
              }, text`}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              currency={AccountStore.getCurrency()}
              numberOfLines={1}
            />
          )}
        </View>
      </View>
      {!!_totalSupFee && (
        <View style={[styles.cartItemMainKey, scanAndPayTemplate.supMargin]}>
          <View
            style={scanAndPayTemplate.checkoutStyles.cartDescriptionSubView}
          >
            <View style={[styles.cartTagIcon, styles.supIconMargin]}>
              <SupLeafIcon />
            </View>
            <View
              style={styles.supTextView}
              accessible
              testID="qa_Checkout.SUPFeeLabel_txt"
              nativeID="qa_Checkout.SUPFeeLabel_txt"
            >
              <AVText
                accessibilityLabel={Localized.Labels.sup_fee}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                style={styles.supDescription}
                numberOfLines={1}
              >
                {Localized.Labels.sup_fee}
              </AVText>
            </View>
          </View>
          <AVFormattedCurrency
            style={styles.priceAmountLine}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
            numberOfLines={1}
            accessible={true}
            accessibilityLabel={`Cart SUP amount $${_totalSupFee}`}
            aria-label={`Cart SUP amount $${_totalSupFee}, text`}
            value={_totalSupFee}
            currency={AccountStore.getCurrency()}
            testID="qa_Checkout.SUPFeeValue_txt"
            nativeID="qa_Checkout.SUPFeeValue_txt"
          />
        </View>
      )}
      <View style={styles.container}>
        <View style={styles.supItemView}>{items}</View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  priceAmountLine: {
    color: Styles.Colors.PayNew.black01,
    fontWeight: '400',
    fontSize: getLayoutDescriber().subHeaderFontSize(),
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  promoDesc: {
    color: scanAndPayTemplate.discountTagColor,
    fontWeight: '400',
    fontSize: scanAndPayTemplate.promoFontSize,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  amount: {
    textDecorationLine: scanAndPayTemplate.priceDecoration,
    fontWeight: '400',
    fontSize: scanAndPayTemplate.cartItemPrice,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  container: {
    alignSelf: 'stretch',
    marginBottom: Styles.Spacing.m1,
    marginTop: Styles.Spacing.m2,
    marginRight: Styles.Spacing.m2,
  },
  items: {
    flexDirection: 'row',
  },
  cartSupLine: {
    fontWeight: '400',
    color: Styles.Colors.PayNew.black01,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  categoryButton: {
    backgroundColor: scanAndPayTemplate.bundleItemsBackgroundColor,
    borderRadius: Styles.Spacing.m4,
    height: undefined,
    marginRight: Styles.Spacing.m2,
    paddingHorizontal: Styles.Spacing.m2 + Styles.Spacing.m1,
    paddingVertical: Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m1,
  },
  promoContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingTop: 10,
  },
  column: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  promoNameItemName: {
    flex: 1,
    justifyContent: 'center',
    left: 8,
    flexDirection: 'row',
    paddingTop: 2,
  },
  promoTagIcon: {
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  promoNameDesc: {
    flex: 1,
    flexDirection: 'column',
    left: 5,
  },
  promoAmountMainView: {
    flex: 0.4,
    justifyContent: 'center',
    top: -2,
    alignItems: 'flex-end',
    rowGap: 5,
  },
  supMainView: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  promoText: {
    fontWeight: '400',
    color: Styles.Colors.PayNew.black01,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  supItemView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    maxWidth: '100%',
    gap: 10,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '100%',
    flexShrink: 1,
    marginLeft: 5,
  },
  cartTagIcon: {
    justifyContent: 'center',
    alignSelf: 'flex-start',
    marginLeft: 4,
  },
  supIconMargin: {
    marginTop: 4,
  },
  supDescription: {
    justifyContent: 'center',
    fontFamily: scanAndPayTemplate.textFontFamily,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    lineHeight: 18,
    color: Styles.Colors.PayNew.black01,
    flex: 0.8,
    marginHorizontal: 2,
  },
  supTextView: {
    flex: 0.7,
    flexDirection: 'row',
    left: 5,
  },
  tagText: {
    fontWeight: '400',
    color: scanAndPayTemplate.prepMethodSelectedText,
    fontSize: scanAndPayTemplate.selectedProductsFont,
    fontFamily: scanAndPayTemplate.textFontFamily,
    marginRight: 5,
    flexShrink: 1,
    overflow: 'hidden',
  },
  promoDeleteIcon: {
    justifyContent: 'center',
    alignSelf: 'center',
    marginRight: 3,
  },
  cartItemMainKey: {
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
  },
});
export default CartItemBundle;
